import { format, formatDistanceToNow, parseISO } from 'date-fns';

export function formatDate(date: string | Date): string {
  if (!date) return '';
  try {
    const d = typeof date === 'string' ? parseISO(date) : date;
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(d);
  } catch {
    return '';
  }
}

export function formatDateTime(date: string | Date): string {
  if (!date) return '';
  try {
    const d = typeof date === 'string' ? parseISO(date) : date;
    // Use Intl to format in the user's browser timezone with timezone abbreviation
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(d);
  } catch {
    return '';
  }
}

export function formatRelativeTime(date: string | Date): string {
  if (!date) return '';
  try {
    const d = typeof date === 'string' ? parseISO(date) : date;
    return formatDistanceToNow(d, { addSuffix: true });
  } catch {
    return '';
  }
}

export function formatDuration(seconds: number): string {
  const s = seconds || 0;
  if (s < 60) return `${Math.round(s)}s`;
  const mins = Math.floor(s / 60);
  const secs = Math.round(s % 60);
  if (mins < 60) return `${mins}m ${secs}s`;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return `${hours}h ${remainingMins}m`;
}

export function formatDurationMs(ms: number): string {
  return formatDuration((ms || 0) / 1000);
}

export function extractPath(url: string | undefined | null): string {
  if (!url) return '/';
  try {
    const parsed = new URL(url);
    return parsed.pathname || '/';
  } catch {
    // If it's already a path (starts with /), return as-is
    if (url.startsWith('/')) return url;
    return url;
  }
}

export function formatNumber(value: number): string {
  const v = value || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toLocaleString();
}

export function formatPercent(value: number): string {
  return `${(value || 0).toFixed(1)}%`;
}

export function formatPercentChange(value: number): string {
  const v = value || 0;
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(1)}%`;
}

export function truncateUrl(url: string | undefined | null, maxLength = 40): string {
  if (!url) return '/';
  if (url.length <= maxLength) return url;
  const start = url.slice(0, maxLength - 3);
  return `${start}...`;
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function generateVisitorId(): string {
  return Math.random().toString(36).substring(2, 8).toUpperCase();
}
